import { useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Table,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import createMemo from 'modules/core/hooks/createMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'
import { matchText } from 'modules/core/lib/stringUtils'
import users from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import TemplateDeleteModal from 'modules/app_store/pages/QuestionsTemplates/TemplateDeleteModal'
import questionsTemplates from 'modules/surveys/store/collections/questionsTemplates'
import type QuestionsTemplate from 'modules/surveys/store/models/QuestionsTemplate'

const NO_AUTHOR = 'Factorial HR'
interface RouteObject {
  edit: string
}

function questionsTemplateRoutes(
  questionsTemplate: QuestionsTemplate,
  navigation: Navigation<NavigationPaths>
): RouteObject {
  if (questionsTemplate.isReview) {
    return {
      edit: navigation.settings.performance_reviews.template.fullPath({
        questionsTemplateId: questionsTemplate.attributes.id,
      }),
    }
  }

  return {
    edit: navigation.settings.surveys.template.fullPath({
      questionsTemplateId: questionsTemplate.attributes.id,
    }),
  }
}

interface SettingsProps {
  questionsTemplate: QuestionsTemplate
  onClickDelete: () => void
}

const Settings = ({ questionsTemplate, onClickDelete }: SettingsProps) => {
  const defaultTemplate = questionsTemplate.isDefault
  const [open, toggle] = useToggle()

  const duplicateTemplate = async () => {
    app.setProgress({
      message: i18n.t('surveys.index.table.row.edit.duplicating'),
    })

    const { success, value } = await asyncAction(() => questionsTemplate.rpc('duplicate_template'))
    app.setProgress(null)

    if (success) {
      questionsTemplates.add([value])
    }
  }

  const popoverOptions = () => {
    const options: Option[] = [
      {
        title: i18n.t(
          'app_store_v2.apps.performance_v2.settings.templates.table.duplicate_template'
        ),
        onClick: duplicateTemplate,
      },
    ]

    if (!defaultTemplate) {
      options.push({
        title: i18n.t('app_store_v2.apps.performance_v2.settings.templates.table.delete_template'),
        type: 'wrong',
        onClick: onClickDelete,
      })
    }

    return options
  }

  return (
    <Popover attachment="top center" open={open} toggle={toggle}>
      <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
      <PopoverContent onClose={toggle(false)} options={popoverOptions()} />
    </Popover>
  )
}

const BlankSlate = () => (
  <Table.Row>
    <Table.Cell colSpan={7}>
      <Box width="full" alignItems="center" justifyContent="center">
        <Text.Normal>
          {i18n.t('app_store_v2.apps.performance_v2.settings.templates.table.blank_slate')}
        </Text.Normal>
      </Box>
    </Table.Cell>
  </Table.Row>
)

interface ItemProps {
  questionsTemplate: QuestionsTemplate
}

const Item = ({ questionsTemplate }: ItemProps) => {
  const { navigation } = useNavigation()
  const [open, toggle] = useToggle(false)
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false)
  const onClose = () => setShowDeleteTemplateModal(false)

  return (
    <>
      <Table.Row>
        <Table.Cell colSpan={3}>
          <Box flexDirection="row">
            <Text.Normal>{questionsTemplate.attributes.title}</Text.Normal>
            {questionsTemplate?.isCore && (
              <Tooltip.Alt
                attachment="top center"
                label={i18n.t(
                  'app_store_v2.apps.performance_v2.settings.templates.table.disclaimer'
                )}
                open={open}
                toggle={toggle}
              >
                <Icon.Medium icon="lock-locked" />
              </Tooltip.Alt>
            )}
          </Box>
        </Table.Cell>
        <Table.Cell colSpan={2}>
          <Text.Normal>
            {users.get(questionsTemplate.attributes.access_id)?.fullName() || NO_AUTHOR}
          </Text.Normal>
        </Table.Cell>
        <Table.Cell colSpan={1}>
          <Text.Normal>{questionsTemplate.attributes.questions_count}</Text.Normal>
        </Table.Cell>
        <Table.Cell colSpan={1}>
          <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
            <Settings
              questionsTemplate={questionsTemplate}
              onClickDelete={() => setShowDeleteTemplateModal(true)}
            />
            <Button.Router
              type="secondary"
              to={questionsTemplateRoutes(questionsTemplate, navigation).edit}
              iconName="arrow-right"
            />
          </Box>
        </Table.Cell>
      </Table.Row>
      {showDeleteTemplateModal && (
        <TemplateDeleteModal
          onClose={onClose}
          onCancel={onClose}
          navigatePathOnDeleted={
            questionsTemplate.isReview
              ? navigation.settings.performance_reviews.fullPath()
              : navigation.settings.surveys.fullPath()
          }
          questionsTemplateId={questionsTemplate.attributes.id}
        />
      )}
    </>
  )
}

const useFilter = createMemo((questionsTemplatesList: QuestionsTemplate[], search: string) =>
  questionsTemplatesList.filter((questionsTemplate) =>
    matchText(search, questionsTemplate.attributes.title)
  )
)
interface BodyProps {
  search: string
  questionsTemplatesList: QuestionsTemplate[]
}

function Body({ questionsTemplatesList, search }: BodyProps) {
  const filteredQuestionsTemplates = useFilter(questionsTemplatesList, search)

  if (!filteredQuestionsTemplates.length) return <BlankSlate />

  const items = filteredQuestionsTemplates.map((questionsTemplate) => (
    <Item key={questionsTemplate.get('id')} questionsTemplate={questionsTemplate} />
  ))

  return <>{items}</>
}

export default observer(Body)
